/**
 * Comments (C13, P5-05): the queue « À traiter », the hidden ones (and why), the AI's answers.
 * A person takes over at any time: public answer, one private answer, hide or show again.
 */

import {
  type SocialComment,
  type SocialCommentStatusEnum,
  useSocialCommentsHideCreate,
  useSocialCommentsList,
  useSocialCommentsPrivateReplyCreate,
  useSocialCommentsReplyCreate,
  useSocialCommentsResolveCreate,
  useSocialCommentsUnhideCreate,
} from "@agencyos/api-client";
import { Avatar, Badge, Button, GlassCard, SegmentedControl, Skeleton, TextAreaField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { dateTimeText, Pager } from "../billing/shared";
import { commentTone, useRefreshSocial, useSocialRoles } from "./shared";

type Filter = "needs_human" | "hidden" | "answered" | "all";
const FILTERS: Filter[] = ["needs_human", "hidden", "answered", "all"];

function Reason({ comment }: { comment: SocialComment }) {
  const { t } = useTranslation();
  const parts: string[] = [];
  if (comment.category) parts.push(t(`social.comments.categories.${comment.category}`));
  if (comment.moderation_source) parts.push(t(`social.comments.sources.${comment.moderation_source}`));
  if (comment.matched_term) parts.push(t("social.comments.term", { term: comment.matched_term }));
  if (comment.ai_confidence !== null) parts.push(t("social.comments.confidence", { value: Math.round(comment.ai_confidence * 100) }));
  if (!parts.length && !comment.handoff_reason) return null;
  return (
    <p className="muted">
      {parts.join(" · ")}
      {comment.handoff_reason ? (
        <>
          <br />
          {t("social.comments.why", { reason: comment.handoff_reason })}
        </>
      ) : null}
    </p>
  );
}

function Answer({ comment, kind, onDone }: { comment: SocialComment; kind: "public" | "private"; onDone: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshSocial();
  const [text, setText] = useState("");
  const options = {
    mutation: {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  };
  const publicReply = useSocialCommentsReplyCreate(options);
  const privateReply = useSocialCommentsPrivateReplyCreate(options);
  const send = kind === "public" ? publicReply : privateReply;
  const errors = fieldErrors(send.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    send.mutate({ id: comment.id, data: { text: text.trim() } });
  };
  const label = kind === "public" ? t("social.comments.publicAnswer") : t("social.comments.privateAnswer");
  return (
    <form className="stack" onSubmit={onSubmit} noValidate aria-label={label}>
      <TextAreaField label={label} value={text} rows={3} maxLength={2000} error={errors.text} onChange={(e) => setText(e.target.value)} />
      <div className="row">
        <Button type="submit" disabled={send.isPending || !text.trim()}>
          {t("social.comments.send")}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

function CommentCard({ comment }: { comment: SocialComment }) {
  const { t, i18n } = useTranslation();
  const { canWork } = useSocialRoles();
  const refresh = useRefreshSocial();
  const [answering, setAnswering] = useState<"public" | "private" | null>(null);
  const options = { mutation: { onSettled: refresh } };
  const hide = useSocialCommentsHideCreate(options);
  const unhide = useSocialCommentsUnhideCreate(options);
  const resolve = useSocialCommentsResolveCreate(options);
  const busy = hide.isPending || unhide.isPending || resolve.isPending;
  return (
    <li className={`social-comment social-comment--${comment.status}`}>
      <div className="row between">
        <span className="social-comment__who">
          <Avatar name={comment.author_name || "?"} />
          <strong>{comment.author_name || t("social.comments.anonymous")}</strong>
        </span>
        <span className="row">
          <Badge>{t(`social.networks.${comment.network}`)}</Badge>
          <Badge tone={commentTone(comment.status)}>{t(`social.comments.statuses.${comment.status}`)}</Badge>
          {comment.test_mode ? <Badge tone="offer">{t("social.mode.testBadge")}</Badge> : null}
        </span>
      </div>
      <small className="muted">{dateTimeText(comment.received_at, i18n.language)}</small>
      <p className="social-comment__text">{comment.text || "—"}</p>
      <Reason comment={comment} />
      {comment.reply_text ? (
        <blockquote className="social-comment__reply">
          <small className="muted">{comment.replied_by_ai ? t("social.comments.aiAnswer") : t("social.comments.teamAnswer", { name: comment.handled_by_name || "—" })}</small>
          <p>{comment.reply_text}</p>
        </blockquote>
      ) : null}
      {comment.private_reply_text ? <p className="muted">{t("social.comments.privateSent", { text: comment.private_reply_text })}</p> : null}
      {canWork && answering ? <Answer comment={comment} kind={answering} onDone={() => setAnswering(null)} /> : null}
      {canWork && !answering ? (
        <div className="row">
          <Button variant="secondary" disabled={busy} onClick={() => setAnswering("public")}>
            {t("social.comments.reply")}
          </Button>
          {!comment.private_reply_text ? (
            <Button variant="ghost" disabled={busy} onClick={() => setAnswering("private")}>
              {t("social.comments.privateReply")}
            </Button>
          ) : null}
          {comment.status === "hidden" ? (
            <Button variant="ghost" disabled={busy} onClick={() => unhide.mutate({ id: comment.id })}>
              {t("social.comments.unhide")}
            </Button>
          ) : (
            <Button variant="ghost" disabled={busy} onClick={() => hide.mutate({ id: comment.id })}>
              {t("social.comments.hide")}
            </Button>
          )}
          {comment.status === "needs_human" ? (
            <Button variant="ghost" disabled={busy} onClick={() => resolve.mutate({ id: comment.id })}>
              {t("social.comments.resolve")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function CommentsTab() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("needs_human");
  const [page, setPage] = useState(1);
  const status: SocialCommentStatusEnum | undefined = filter === "all" ? undefined : filter;
  const list = useSocialCommentsList({ page, ...(status ? { status } : {}) });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  let content;
  if (list.isPending) content = <Skeleton />;
  else if (!data) content = <p role="alert">{t("common.error")}</p>;
  else if (!data.results.length) content = <p className="muted">{t(`social.comments.empty.${filter}`)}</p>;
  else
    content = (
      <>
        <ul className="plain-list stack" aria-label={t(`social.comments.filters.${filter}`)}>
          {data.results.map((comment) => (
            <CommentCard key={comment.id} comment={comment} />
          ))}
        </ul>
        <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} labels={{ previous: t("social.previous"), next: t("social.next"), page: t("social.page", { page }) }} />
      </>
    );
  return (
    <GlassCard as="section" className="stack" aria-labelledby="social-comments-title">
      <h2 id="social-comments-title">{t("social.comments.title")}</h2>
      <p className="muted">{t("social.comments.intro")}</p>
      <SegmentedControl
        label={t("social.comments.filterLabel")}
        value={filter}
        options={FILTERS.map((value) => ({ value, label: t(`social.comments.filters.${value}`) }))}
        onChange={(value) => {
          setFilter(value);
          setPage(1);
        }}
      />
      {content}
    </GlassCard>
  );
}
